/**
 * סל אופליין: שמירה ב־localStorage וסנכרון לשרת כשחוזר חיבור.
 * מאפשר לערוך את הסל במלואו גם ללא אינטרנט.
 */

const PREFIX = "reshet_offline_cart_";

/** מזהה סניף יציב (מחרוזת) — אובייקטים/ ObjectId לא יוצרים מפתח localStorage שגוי */
export function normalizeStoreId(storeId) {
  if (storeId == null || storeId === "") return "";
  if (typeof storeId === "object") {
    const id = storeId._id ?? storeId.$oid ?? storeId.id;
    return id != null ? String(id) : "";
  }
  return String(storeId);
}

function getKey(storeId) {
  const sid = normalizeStoreId(storeId);
  return `${PREFIX}${sid || ""}`;
}

function productIdKey(item) {
  if (!item) return "";
  return String(item.product_id?._id ?? item.product_id?.id ?? item.product_id ?? "");
}

/**
 * השרת = מקור לפריטים שקיימים בו; פריטים שקיימים רק בלוקאל (הוספה offline-first לפני POST) נשמרים.
 * בלי זה תשובת שרת "ישנה" דורסת את localStorage ומוחקת הוספות שלא הועלו לשרת.
 */
export function mergeServerCartWithOffline(serverList, offlineList) {
  const server = Array.isArray(serverList) ? serverList : [];
  const offline = Array.isArray(offlineList) ? offlineList : [];
  const merged = new Map();
  for (const item of server) {
    const k = productIdKey(item);
    if (k) merged.set(k, item);
  }
  for (const item of offline) {
    const k = productIdKey(item);
    if (k && !merged.has(k)) merged.set(k, item);
  }
  return Array.from(merged.values());
}

/**
 * @param {string} storeId
 * @returns {Array<{ _id: string, product_id: object|string, quantity: number, store_id: string, supplier_id?: string }>}
 */
export function getOfflineCart(storeId) {
  const sid = normalizeStoreId(storeId);
  if (!sid) return [];
  try {
    const raw = localStorage.getItem(getKey(sid));
    if (!raw) return [];
    const data = JSON.parse(raw);
    return Array.isArray(data) ? data : [];
  } catch {
    return [];
  }
}

/**
 * @param {string} storeId
 * @param {Array} items
 */
export function setOfflineCart(storeId, items) {
  const sid = normalizeStoreId(storeId);
  if (!sid) return;
  try {
    localStorage.setItem(getKey(sid), JSON.stringify(items || []));
  } catch (e) {
    console.warn("offlineCart.setOfflineCart failed", e);
  }
}

/**
 * מוסיף או מעדכן פריט בסל האופליין. מחזיר את הרשימה המעודכנת.
 * שומר גם supplier_name כדי להציג את שם הספק באופליין.
 * @param {string} storeId
 * @param {{ product_id: string, quantity: number, store_id: string, supplier_id?: string, supplier_name?: string, product?: object }} item
 * @returns {Array}
 */
export function addOrUpdateOfflineCartItem(storeId, item) {
  const sid = normalizeStoreId(storeId);
  const list = getOfflineCart(sid);
  const productId = String(item.product_id?._id || item.product_id);
  const idx = list.findIndex(
    (i) => String(i.product_id?._id || i.product_id) === productId
  );
  const supplierName = item.supplier_name ?? item.supplier?.name ?? null;
  const newItem = {
    _id: `offline-${productId}`,
    product_id: item.product || item.product_id,
    quantity: Number(item.quantity) || 1,
    store_id: normalizeStoreId(item.store_id) || sid,
    supplier_id: item.supplier_id ?? null,
    supplier_name: supplierName != null ? supplierName : (idx >= 0 ? list[idx].supplier_name : null),
  };
  if (idx >= 0) {
    if (newItem.quantity <= 0) {
      list.splice(idx, 1);
    } else {
      list[idx] = { ...list[idx], ...newItem };
    }
  } else if (newItem.quantity > 0) {
    list.push(newItem);
  }
  setOfflineCart(sid, list);
  return list;
}

/**
 * @param {string} storeId
 * @param {string} productId
 */
export function removeOfflineCartItem(storeId, productId) {
  const sid = normalizeStoreId(storeId);
  const list = getOfflineCart(sid).filter(
    (i) => String(i.product_id?._id || i.product_id) !== String(productId)
  );
  setOfflineCart(sid, list);
  return list;
}

/**
 * מנקה את סל האופליין אחרי סנכרון מוצלח.
 * @param {string} storeId
 */
export function clearOfflineCart(storeId) {
  const sid = normalizeStoreId(storeId);
  if (!sid) return;
  try {
    localStorage.removeItem(getKey(sid));
  } catch {}
}

export function isNetworkError(err) {
  if (!err) return false;
  const msg = (err.message || "").toLowerCase();
  const code = err?.code ?? err?.errno;
  return (
    msg.includes("network") ||
    msg.includes("failed to fetch") ||
    msg.includes("internet") ||
    msg.includes("timeout") ||
    msg.includes("abort") ||
    code === "ERR_NETWORK" ||
    code === "ECONNABORTED" ||
    code === "ERR_CANCELED" ||
    code === "ABORT_ERR"
  );
}

/**
 * מחזיר את רשימת הסל: מהשרת כשמחוברים, מהמטמון המקומי כשמתחת.
 * לשימוש ב־queryFn: () => getCartItemsWithOffline(storeId)
 */
export async function getCartItemsWithOffline(storeId, apiListFn) {
  const sid = normalizeStoreId(storeId);
  if (!sid) return [];
  const offline = getOfflineCart(sid);
  if (typeof navigator !== "undefined" && !navigator.onLine) {
    return offline;
  }
  try {
    const data = await apiListFn();
    const list = Array.isArray(data) ? data : (data?.data && Array.isArray(data.data) ? data.data : []);
    const merged = mergeServerCartWithOffline(list, offline);
    if (merged.length > 0) setOfflineCart(sid, merged);
    return merged;
  } catch (e) {
    if (isNetworkError(e)) return offline.length ? offline : getOfflineCart(sid);
    throw e;
  }
}

/**
 * מסנכרן את סל האופליין לשרת (לאחר חזרת חיבור). אחרי הדחיפה מעדכן את הלוקאל מהשרת.
 * @param {string} storeId
 * @param {{ queryClient?: import("@tanstack/react-query").QueryClient, updateCartItem: (payload) => Promise<any>, fetchList?: () => Promise<any> }} deps
 */
export async function syncOfflineCartToServer(storeId, { queryClient, updateCartItem, fetchList }) {
  const sid = normalizeStoreId(storeId);
  const items = getOfflineCart(sid);
  if (!sid || !items.length) return;
  for (const item of items) {
    const productId = item.product_id?._id ?? item.product_id;
    if (!productId) continue;
    try {
      await updateCartItem({
        product_id: productId,
        store_id: sid,
        quantity: item.quantity ?? 1,
        supplier_id: item.supplier_id ?? undefined,
      });
    } catch (e) {
      console.warn("offlineCart sync item failed", productId, e);
    }
  }
  if (typeof fetchList === "function") {
    try {
      const list = await fetchList();
      const arr = Array.isArray(list) ? list : (list?.data ?? []);
      const offlineAfter = getOfflineCart(sid);
      setOfflineCart(sid, mergeServerCartWithOffline(arr, offlineAfter));
    } catch (e) {
      console.warn("offlineCart fetch after sync failed", e);
    }
  }
  if (queryClient) queryClient.invalidateQueries({ queryKey: ["cartItems", sid] });
}
